import { Component, Input, OnInit, OnDestroy, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { combineLatest, Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { SubscriberService } from 'src/app/services/manager/subscriber.service';
import { OfflineService } from 'src/app/services/manager/offline.service';
import { StorageService } from 'src/app/services/data/storage.service';
import { LoginService } from 'src/app/services/auth/login.service';
import { OrderService } from 'src/app/services/manager/order.service';
import { Order } from 'src/app/interfaces/orders';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderDetailsComponent implements OnInit, OnDestroy {
  destroy = new Subject();
  @Input() search = "";
  @Input() showSearch = true;

  private searchSubject = new Subject<string>();

  searchTerm = '';

  onlineList: any = [];
  offlineList: any = [];

  dataList: any;
  dataListFiltered: any;

  hideCustomerNames = false;
  isAdmin = false;

  constructor(
    private sub: SubscriberService,
    private offline: OfflineService,
    private storage: StorageService,
    private login: LoginService,
    private orderService: OrderService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.storage.getData<boolean>('hideCustomerNames').then(v => {
      this.hideCustomerNames = v === true;
      this.cdr.markForCheck();
    });
    this.isAdmin = this.login.decodedToken?.role === 'admin';

    this.searchSubject.pipe(
      takeUntil(this.destroy),
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchTerm = term;
      this.filterProducts();
    });

    this.subscribe();
  }

  ngOnDestroy() {
    this.destroy.next(null);
    this.destroy.complete();
  }

      ngOnChanges(changes: SimpleChanges) {
        if (changes['search']) {
          this.handleDataChange(changes['search'].currentValue);
        }
      }

      handleDataChange(newValue: string) {
          this.searchTerm = newValue;
          this.filterProducts();
      }

  subscribe() {
    // Kombiniere beide Observables, damit mergeOrders() nur einmal aufgerufen wird
    // wenn beide Listen aktualisiert wurden
    combineLatest([
      this.sub.orderList,
      this.sub.offlineOrders
    ])
    .pipe(
      debounceTime(100), // Optimierte Verzögerung für schnellere UI-Updates
      takeUntil(this.destroy)
    )
    .subscribe(([onlineData, offlineData]) => {
      this.onlineList = onlineData;
      this.offlineList = offlineData;
      this.mergeOrders();
      this.filterProducts();
      this.cdr.markForCheck();
    });
  }

  newOrder() {
    this.offline.createOfflineOrder(true);
  }

  mergeOrders() {
    // Sicherstellen, dass beide Variablen Arrays sind
    const onlineList = Array.isArray(this.onlineList) ? this.onlineList : [];
    const offlineList = Array.isArray(this.offlineList) ? this.offlineList : [];

    const addMeta = (order: any, sync: boolean) => {
      const createdMs = order?.created ? new Date(order.created).getTime() : 0;
      const updatedMs = order?.updated ? new Date(order.updated).getTime() : 0;
      return { ...order, sync, createdMs, updatedMs };
    };

    // Markiere Online-Orders mit sync: true
    const onlineWithSync = onlineList.map((order: any) => addMeta(order, true));

    // Markiere Offline-Orders mit sync: false
    const offlineWithSync = offlineList.map((order: any) => addMeta(order, false));

    // Kombinieren beider Listen
    const combinedList = [...onlineWithSync, ...offlineWithSync];

    // Erstelle eine Map für schnelleren Zugriff und automatisches Merging
    const orderMap = new Map<string, any>();

    for (const order of combinedList) {
      const existingOrder = orderMap.get(order.orderId);

      if (existingOrder) {
        const currentUpdated = order.updatedMs ?? 0;
        const existingUpdated = existingOrder.updatedMs ?? 0;

        // Behalte den Order mit dem neueren Timestamp
        if (currentUpdated > existingUpdated) {
          orderMap.set(order.orderId, order);
        }
        // Wenn Timestamps gleich sind, bevorzuge Offline-Order (sync: false)
        else if (currentUpdated === existingUpdated && order.sync === false) {
          orderMap.set(order.orderId, order);
        }
      } else {
        // Neuer Order, füge hinzu
        orderMap.set(order.orderId, order);
      }
    }

    // Konvertiere Map zurück zu Array
    this.dataList = Array.from(orderMap.values());
  }

  onSearchChange(event: any) {
    const inputValue = event.target.value;
    this.searchSubject.next(inputValue);
  }

  setCurrentOrder(order: any) {
    this.offline.setCurrentOrder(order);
  }


  filterProducts() {
    if(this.dataList == null || this.dataList == undefined) {
      return;
    }

    const baseList = Array.isArray(this.dataList) ? this.dataList : [];

    if (this.searchTerm) {
      const searchTermLower = this.searchTerm.toLowerCase().trim();

      this.dataListFiltered = baseList
        .filter((item: any) => item.status === 0)
        .filter((item: any) => {
          // Basis-Felder
          const searchableFields = ['orderId', 'orderNumber', 'orderTitle', 'customerEmail', 'deliveryNote'];
          
          // Prüfe Basis-Felder
          const basicMatch = searchableFields.some(field => {
            const value = item[field];
            return value != null && value.toString().toLowerCase().includes(searchTermLower);
          });

          if (basicMatch) return true;

          // Prüfe customerBase Felder
          if (item.customerBase) {
            const customerBaseFields = [
              item.customerBase.companyName,
              item.customerBase.email,
              item.customerBase.phone,
              item.customerBase.contactPerson?.firstName,
              item.customerBase.contactPerson?.lastName,
              item.customerBase.address?.street,
              item.customerBase.address?.city,
              item.customerBase.address?.zip
            ];

            const customerBaseMatch = customerBaseFields.some(value => 
              value != null && value.toString().toLowerCase().includes(searchTermLower)
            );

            if (customerBaseMatch) return true;
          }

          // Prüfe customerAddress Felder
          if (item.customerAddress) {
            const customerAddressFields = [
              item.customerAddress.name,
              item.customerAddress.street,
              item.customerAddress.city,
              item.customerAddress.zip
            ];

            const customerAddressMatch = customerAddressFields.some(value => 
              value != null && value.toString().toLowerCase().includes(searchTermLower)
            );

            if (customerAddressMatch) return true;
          }

          return false;
        })
        .sort((a: any, b: any) => (b.createdMs ?? 0) - (a.createdMs ?? 0));
    } else {
      this.dataListFiltered = baseList
        .filter((item: any) => item.status === 0)
        .sort((a: any, b: any) => (b.createdMs ?? 0) - (a.createdMs ?? 0));
    }
  }

  trackByOrderId(index: number, order: any): string {
    return order.orderId || index.toString();
  }

  getDeliveryStatusTitle(status: string | null | undefined): string {
    switch (status) {
      case 'full': return 'Vollständig geliefert';
      case 'partial': return 'Teilweise geliefert';
      default: return 'Keine Lieferung';
    }
  }

  /** Farbe des Abrechnungs-Prozentsatz-Badges: blau = manuell übersteuert (kein realer 100%-
   * Stand), grün = rechnerisch komplett, gelb = teilweise, grau = noch nichts abgerechnet. */
  invoicedBadgeColor(order: Order): string {
    if (order.invoicingClosedManually) return 'tertiary'; // blau in der Ionic-Palette
    const pct = order.invoicedPercent ?? 0;
    if (pct >= 100) return 'success';
    if (pct > 0) return 'warning';
    return 'medium';
  }

  invoicedBadgeLabel(order: Order): string {
    if (order.invoicingClosedManually) return 'Manuell abgeschlossen';
    return `${order.invoicedPercent ?? 0}% abgerechnet`;
  }

  async toggleInvoicingClosed(order: Order, event: Event): Promise<void> {
    event.stopPropagation();
    const success = order.invoicingClosedManually
      ? await this.orderService.reopenOrderInvoicing(order)
      : await this.orderService.closeOrderInvoicingManually(order);

    if (success) {
      order.invoicingClosedManually = !order.invoicingClosedManually;
      this.cdr.markForCheck();
    }
  }
}
